'use client';

import { Phone, Loader2, AlertCircle, X } from 'lucide-react';
import { SlideCaptcha } from '@/src/components/ui/slide-captcha';
import { createPortal } from 'react-dom';
import { cn } from '@/lib/utils';
import { Z } from '@/lib/z-index';
import type { OtpState, AuthFormState } from '@/lib/hooks/use-auth-form';
import { LoginTabAccount } from './login-tab-account';

const PHONE_FORMAT_RE = /^(\d{3})(\d{4})(\d{4})$/;

interface RegisterTabProps {
  regTab: 'username' | 'phone';
  phone: string;
  setPhone: (v: string) => void;
  username: string;
  setUsername: (v: string) => void;
  password: string;
  setPassword: (v: string) => void;
  showPassword: boolean;
  setShowPassword: (v: boolean) => void;
  phoneRegStep: 1 | 2;
  setPhoneRegStep: (v: 1 | 2) => void;
  otp: OtpState;
  form: AuthFormState;
  onSubmit: (e: React.FormEvent) => void;
  onCodeComplete: (code: string) => void;
}

/**
 * RegisterTab — registration form supporting both username+password and phone+OTP flows.
 */
export function RegisterTab({
  regTab,
  phone,
  setPhone,
  username,
  setUsername,
  password,
  setPassword,
  showPassword,
  setShowPassword,
  phoneRegStep,
  setPhoneRegStep,
  otp,
  form,
  onSubmit,
  onCodeComplete,
}: RegisterTabProps) {
  const { loading, error } = form;

  // Username tab reuses LoginTabAccount with mode="register"
  if (regTab === 'username') {
    return (
      <LoginTabAccount
        form={form}
        mode="register"
        onSubmit={onSubmit}
        password={password}
        setPassword={setPassword}
        setShowPassword={setShowPassword}
        setUsername={setUsername}
        showPassword={showPassword}
        username={username}
      />
    );
  }

  // 滑块验证弹窗
  const slideCaptchaOverlay = otp.slideCaptcha
    ? createPortal(
      <div
        className="fixed inset-0 flex items-center justify-center overflow-y-auto bg-black/40 p-4 animate-in fade-in duration-200"
        style={{ zIndex: Z.MODAL_NESTED }}
      >
        <div className="relative w-full max-w-[460px] bg-background rounded-2xl shadow-xl p-5 animate-in zoom-in-95 duration-200">
          <button
            className="absolute top-3 right-3 p-1 rounded-lg text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
            onClick={otp.cancelSlideCaptcha}
            type="button"
          >
            <X className="h-4 w-4" />
          </button>
          <p className="text-sm font-medium text-foreground mb-4 pr-6">请完成安全验证</p>
          <SlideCaptcha
            captcha={otp.slideCaptcha}
            disabled={otp.smsLoading}
            onRefresh={otp.refreshSlideCaptcha}
            onVerify={otp.handleSlideVerify}
            status={otp.slideCaptchaStatus}
          />
        </div>
      </div>,
      document.body,
    )
    : null;

  // Phone tab — step 1: phone input
  if (phoneRegStep === 1) {
    return (
      <div className="space-y-5">
        <div className="space-y-2">
          <label className="text-xs font-medium text-muted-foreground pl-1 tracking-wide" htmlFor="reg-phone">
            手机号码
          </label>
          <div className="relative">
            <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
            <input
              autoComplete="tel"
              className={cn(
                'w-full h-10 pl-10 pr-4 rounded-xl border bg-background text-sm text-foreground placeholder:text-muted-foreground/60 focus:outline-none focus:ring-2 focus:ring-primary/30 transition-all',
                error ? 'border-destructive/50' : 'border-border focus:border-primary/50',
              )}
              id="reg-phone"
              onChange={(e) => {
                setPhone(e.target.value);
                form.setError('');
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  e.preventDefault();
                  otp.handlePhoneNext(phone, 'register', () => setPhoneRegStep(2));
                }
              }}
              placeholder="输入手机号"
              type="tel"
              value={phone}
            />
          </div>
        </div>

        {error && (
          <div className="flex items-center gap-2 rounded-xl border border-destructive/20 bg-destructive/5 px-3.5 py-2.5 animate-in fade-in slide-in-from-top-1 duration-200">
            <AlertCircle className="h-4 w-4 text-destructive flex-shrink-0" />
            <p className="text-xs text-destructive">{error}</p>
          </div>
        )}

        <button
          className="w-full h-10 flex items-center justify-center gap-2 rounded-xl bg-primary text-primary-foreground text-sm font-medium hover:bg-primary/90 disabled:opacity-60 transition-colors mt-1"
          disabled={otp.smsLoading || otp.slideCaptchaLoading || !phone.trim()}
          onClick={() => otp.handlePhoneNext(phone, 'register', () => setPhoneRegStep(2))}
          type="button"
        >
          {otp.smsLoading || otp.slideCaptchaLoading ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              加载中…
            </>
          ) : (
            <>下一步</>
          )}
        </button>
        {slideCaptchaOverlay}
      </div>
    );
  }

  // Phone tab — step 2: OTP verification
  return (
    <div className="space-y-3">
      <div className="flex items-center justify-center gap-2 mb-2">
        <button
          className="flex items-center gap-0.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
          onClick={() => {
            setPhoneRegStep(1);
            form.setError('');
            otp.resetDigits();
          }}
          type="button"
        />
        <span className="text-sm text-foreground font-medium tracking-wide">{phone.trim().replace(PHONE_FORMAT_RE, '$1 $2 $3')}</span>
        <button
          className="text-xs text-primary hover:underline"
          onClick={() => {
            setPhoneRegStep(1);
            form.setError('');
            otp.resetDigits();
          }}
          type="button"
        >
          更换
        </button>
      </div>

      <p className="text-xs text-muted-foreground text-center mb-5">验证码已发送至该手机号</p>

      <div className="flex justify-center gap-2.5 mb-4" onPaste={(e) => otp.handleDigitPaste(e, onCodeComplete)}>
        {otp.codeDigits.map((digit, i) => (
          <input
            className={cn(
              'w-12 h-14 text-center text-2xl font-semibold rounded-xl border-2 bg-background transition-all focus:outline-none',
              loading && 'opacity-50',
              error ? 'border-destructive/50 focus:border-destructive' : digit ? 'border-primary/50 focus:border-primary' : 'border-border focus:border-primary/50',
            )}
            disabled={loading}
            inputMode="numeric"
            key={i}
            maxLength={1}
            onChange={(e) => otp.handleDigitChange(i, e.target.value, onCodeComplete)}
            onKeyDown={(e) => otp.handleDigitKeyDown(i, e)}
            ref={(el) => {
              otp.digitRefs.current[i] = el;
            }}
            type="text"
            value={digit}
          />
        ))}
      </div>

      {loading && (
        <div className="flex items-center justify-center gap-2 py-2 animate-in fade-in duration-200">
          <Loader2 className="h-4 w-4 animate-spin text-primary" />
          <span className="text-sm text-muted-foreground">注册中…</span>
        </div>
      )}

      {error && <p className="text-xs text-destructive text-center mb-2 animate-in fade-in slide-in-from-top-1 duration-200">{error}</p>}

      <div className="text-center mt-2">
        <button
          className="text-xs text-muted-foreground hover:text-foreground disabled:opacity-50 transition-colors"
          disabled={otp.smsCooldown > 0 || otp.smsLoading || otp.slideCaptchaLoading}
          onClick={() => otp.handleSendSms(phone, 'register')}
          type="button"
        >
          {otp.smsCooldown > 0 ? `${otp.smsCooldown}s 后可重新发送` : '重新发送验证码'}
        </button>
      </div>
      {slideCaptchaOverlay}
    </div>
  );
}
